import { LinkComponentProps, useNavigate } from '@tanstack/react-router';
import React from 'react';
import { toast } from 'react-toastify';

import { AppFeatures, useFeature } from '@/app/features/use-feature';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownLabel,
  DropdownMenu
} from '@/common/components/dropdown';
import { Link } from '@/common/components/link';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '@/common/components/navbar';
import { Sidebar, SidebarBody, SidebarItem, SidebarSection } from '@/common/components/sidebar';
import { StackedLayout } from '@/common/components/stacked-layout';
import { ArrowRightFromBracketIcon, FileContractIcon, GearComplexIcon, UserIcon } from '@/icons/fa/regular';
import { BellIcon, CircleUserIcon } from '@/icons/fa/solid';
import { useAuth } from '@/shared/auth/use-auth';
import { CookieConsent } from '@/shared/components/cookie-consent';

import { Toast } from '../components/toast';

type NavItem = {
  label: string;
  to: LinkComponentProps['to'];
};

const navItems: NavItem[] = [
  { label: 'Home', to: '/' },
  { label: 'Billing', to: '/billing' },
  { label: 'Usage', to: '/usage' }
];

export function MainLayout({ children }: { children: React.ReactNode }) {
  const isApplicationsEnabled = useFeature(AppFeatures.Applications);
  const isCookieConsentEnabled = useFeature(AppFeatures.CookieConsent);
  const { user, logout, isAuthenticated, isImpersonating, isError, error } = useAuth();

  const userDetails = user?.data;
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isError) {
      toast.error(Toast, { data: { title: 'Unabled to Load User', content: error?.message ?? 'Please try again later.' } });
    }
  });

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => {
        void navigate({ to: '/login' });
      }
    });
  }

  return (
    <>
      <StackedLayout
        isImpersonating={isImpersonating}
        isAuthenticated={isAuthenticated}
        navbar={(
          <Navbar>
            <Link to="/">
              <img
                src="/img/talquin_logo.svg"
                className="w-[164px]"
                alt="The Talquin Electric Cooperative logo."
              />
            </Link>
            {isAuthenticated && (
              <>
                <NavbarSpacer />
                <NavbarSection className="max-lg:hidden">
                  {navItems.map(({ label, to }) => (
                    <NavbarItem key={label} to={to} current>
                      <span className="uppercase">{label}</span>
                    </NavbarItem>
                  ))}
                </NavbarSection>
                <NavbarSection>
                  <NavbarItem to="/notifications" aria-label="Notifications">
                    <BellIcon/>
                  </NavbarItem>
                  <Dropdown>
                    <DropdownButton as={NavbarItem}>
                      <CircleUserIcon/>
                    </DropdownButton>
                    <DropdownMenu className="min-w-64" anchor="bottom end">
                      <DropdownHeader>
                        <div className="pr-6">
                          <div className="text-2xl text-gray-700">{`${userDetails?.first_name} ${userDetails?.last_name}`}</div>
                          <div className="text-sm/7 font-semibold text-gray-700">{userDetails?.email}</div>
                        </div>
                      </DropdownHeader>
                      <DropdownDivider />
                      <DropdownItem to="/settings/user-profile">
                        <UserIcon />
                        <DropdownLabel>Profile</DropdownLabel>
                      </DropdownItem>
                      <DropdownItem to="/settings">
                        <GearComplexIcon />
                        <DropdownLabel>Settings</DropdownLabel>
                      </DropdownItem>
                      {isApplicationsEnabled && (
                        <DropdownItem to="/applications">
                          <FileContractIcon />
                          <DropdownLabel>My Applications</DropdownLabel>
                        </DropdownItem>
                      )}
                      <DropdownDivider />
                      <DropdownItem onClick={handleLogout}>
                        <ArrowRightFromBracketIcon />
                        <DropdownLabel>Log Out</DropdownLabel>
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </NavbarSection>
              </>
            )}
          </Navbar>
        )}
        sidebar={(
          <Sidebar>
            <SidebarBody>
              <SidebarSection>
                {navItems.map(({ label, to }) => (
                  <SidebarItem key={label} to={to}>
                    {label}
                  </SidebarItem>
                ))}
              </SidebarSection>
            </SidebarBody>
          </Sidebar>
        )}
      >
        {children}
      </StackedLayout>
      {isCookieConsentEnabled && (
        <CookieConsent />
      )}
    </>
  );
}
